/**
 * @file EmailIntakePage.jsx
 * @description Review queue for machine status changes detected from email.
 * Field staff CC iotmtandt@mtandt.com on de-hire / mobilisation / maintenance
 * mails; the server parses them (no AI) and stages each here. A super-admin
 * confirms the machine + target status, and approval applies the change through
 * the same lifecycle path as the manual status control.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, Chip, IconButton, Tooltip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
    Snackbar, Alert, ToggleButtonGroup, ToggleButton, Stack
} from '@mui/material';
import {
    Sync as SyncIcon, Refresh as RefreshIcon, CheckCircle as ApproveIcon,
    Cancel as RejectIcon
} from '@mui/icons-material';
import emailIntakeService from '../../services/emailIntakeService';

const STATUS_OPTIONS = [
    { value: 'RENTAL', label: 'On Rent' },
    { value: 'DE_HIRED', label: 'De-hired' },
    { value: 'SERVICE', label: 'On Service' },
    { value: 'BREAKDOWN', label: 'Breakdown' },
    { value: 'AVAILABLE', label: 'Available (Ready for Rent)' },
    { value: 'WAITING_FOR_RENT', label: 'Waiting for Rent' },
];
const ACTION_LABEL = { DE_HIRE: 'De-hire', MOBILISE: 'Mobilise / New site', MAINTENANCE: 'Maintenance' };
const ACTION_COLOR = { DE_HIRE: 'warning', MOBILISE: 'info', MAINTENANCE: 'secondary' };
const CONF_COLOR = { high: 'success', medium: 'warning', low: 'default' };
const MACHINE_STATE = {
    INSTALLED: { label: 'Telematics ✓', color: 'success' },
    NOT_INSTALLED: { label: 'No telematics', color: 'warning' },
    NOT_FOUND: { label: 'Not in registry', color: 'default' },
};
const ROW_STATUS_COLOR = { PENDING: 'warning', APPLIED: 'success', REJECTED: 'default', FAILED: 'error' };
const FILTERS = ['PENDING', 'APPLIED', 'REJECTED', 'FAILED', 'ALL'];

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function EmailIntakePage() {
    const [filter, setFilter] = useState('PENDING');
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const [approveRow, setApproveRow] = useState(null);
    const [form, setForm] = useState({ equipmentId: '', toStatus: 'DE_HIRED', effectiveDate: '', rentalContractId: '' });
    const [rejectRow, setRejectRow] = useState(null);
    const [rejectNote, setRejectNote] = useState('');
    const [busy, setBusy] = useState(false);

    const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await emailIntakeService.list(filter);
            setRows(res?.data || []);
            setSummary(res?.summary || {});
        } catch (e) {
            notify(e?.response?.data?.message || 'Failed to load requests', 'error');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const res = await emailIntakeService.sync();
            notify(`Inbox scanned: ${res.scanned} mail, ${res.relevant} relevant, ${res.inserted} new request(s) staged.`, 'info');
            await load();
        } catch (e) {
            notify(e?.response?.data?.message || 'Inbox sync failed', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const openApprove = (row) => {
        setApproveRow(row);
        setForm({
            equipmentId: row.equipment_id || '',
            toStatus: row.suggested_status || 'DE_HIRED',
            effectiveDate: row.effective_date ? String(row.effective_date).slice(0, 10) : '',
            rentalContractId: '',
        });
    };

    const submitApprove = async () => {
        if (!form.equipmentId.trim()) { notify('Enter the machine (equipment id) for this request', 'warning'); return; }
        setBusy(true);
        try {
            const isRental = form.toStatus === 'RENTAL' || form.toStatus === 'WAITING_FOR_RENT';
            const payload = {
                equipmentId: form.equipmentId.trim(),
                toStatus: form.toStatus,
                effectiveDate: form.effectiveDate || undefined,
                opening: isRental && form.rentalContractId.trim() ? { rentalContractId: form.rentalContractId.trim() } : {},
            };
            const res = await emailIntakeService.approve(approveRow.id, payload);
            notify(res.message || 'Status applied', 'success');
            setApproveRow(null);
            await load();
        } catch (e) {
            notify(e?.response?.data?.message || 'Failed to apply', 'error');
        } finally {
            setBusy(false);
        }
    };

    const submitReject = async () => {
        setBusy(true);
        try {
            await emailIntakeService.reject(rejectRow.id, rejectNote);
            notify('Request rejected', 'success');
            setRejectRow(null);
            setRejectNote('');
            await load();
        } catch (e) {
            notify(e?.response?.data?.message || 'Failed to reject', 'error');
        } finally {
            setBusy(false);
        }
    };

    const isRentalTarget = form.toStatus === 'RENTAL' || form.toStatus === 'WAITING_FOR_RENT';

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Actions — no page title, the top bar already names the page */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1}>
                    <Button onClick={load} startIcon={<RefreshIcon />} variant="outlined" disabled={loading}>Refresh</Button>
                    <Button onClick={handleSync} startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />} variant="contained" disabled={syncing}>
                        {syncing ? 'Scanning…' : 'Sync Inbox Now'}
                    </Button>
                </Stack>
            </Stack>

            {/* Filter + summary */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
                <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)}>
                    {FILTERS.map((f) => (
                        <ToggleButton key={f} value={f} sx={{ textTransform: 'none', px: 1.5 }}>
                            {f === 'ALL' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
                            {summary[f] != null && f !== 'ALL' ? ` (${summary[f]})` : ''}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: '70vh' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Received</TableCell>
                                <TableCell>From</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Machine</TableCell>
                                <TableCell>Site</TableCell>
                                <TableCell>Confidence</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Review</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                    No {filter === 'ALL' ? '' : filter.toLowerCase()} requests. Click <b>Sync Inbox Now</b> to scan for new mails.
                                </TableCell></TableRow>
                            ) : rows.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(r.received_at)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} noWrap>{r.sender_name || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>{r.sender_email}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        <Tooltip title={r.body_excerpt || ''} placement="top-start">
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {r.subject || '(no subject)'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {r.detected_action
                                            ? <Chip size="small" color={ACTION_COLOR[r.detected_action] || 'default'} label={ACTION_LABEL[r.detected_action] || r.detected_action} />
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5} alignItems="flex-start">
                                            {r.equipment_id
                                                ? <Chip size="small" variant="outlined" label={r.equipment_id} />
                                                : <Chip size="small" color="warning" variant="outlined" label="assign machine" />}
                                            {r.machine_state && MACHINE_STATE[r.machine_state]
                                                ? <Chip size="small" color={MACHINE_STATE[r.machine_state].color} variant="outlined" label={MACHINE_STATE[r.machine_state].label} />
                                                : null}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{r.site || '—'}</TableCell>
                                    <TableCell>
                                        {r.confidence ? <Chip size="small" color={CONF_COLOR[r.confidence] || 'default'} label={r.confidence} /> : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" color={ROW_STATUS_COLOR[r.status] || 'default'} label={r.status} />
                                        {r.status === 'FAILED' && r.note ? (
                                            <Tooltip title={r.note}><Typography variant="caption" color="error" display="block" noWrap sx={{ maxWidth: 160 }}>{r.note}</Typography></Tooltip>
                                        ) : null}
                                    </TableCell>
                                    <TableCell align="right">
                                        {r.status === 'PENDING' || r.status === 'FAILED' ? (
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Approve & apply status change">
                                                    <IconButton size="small" color="success" onClick={() => openApprove(r)}><ApproveIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject / dismiss">
                                                    <IconButton size="small" color="error" onClick={() => setRejectRow(r)}><RejectIcon /></IconButton>
                                                </Tooltip>
                                            </Stack>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                {r.reviewed_by_name ? `by ${r.reviewed_by_name}` : ''}
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Approve dialog */}
            <Dialog open={Boolean(approveRow)} onClose={() => !busy && setApproveRow(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Approve status change</DialogTitle>
                <DialogContent dividers>
                    {approveRow && (
                        <Stack spacing={2} sx={{ mt: 0.5 }}>
                            <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">From {approveRow.sender_email} · {fmt(approveRow.received_at)}</Typography>
                                <Typography variant="subtitle2" fontWeight={700}>{approveRow.subject}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                    {approveRow.body_excerpt}
                                </Typography>
                            </Box>
                            {approveRow.machine_state && approveRow.machine_state !== 'INSTALLED' && (
                                <Alert severity="warning">
                                    {approveRow.machine_state === 'NOT_FOUND'
                                        ? 'No installed telematics machine was matched. You can only apply this if you enter the equipment id of an installed machine.'
                                        : 'The matched machine has no telematics installed. Status can only be changed for installed telematics machines.'}
                                </Alert>
                            )}
                            <TextField
                                label="Machine (equipment id)" value={form.equipmentId} required
                                onChange={(e) => setForm({ ...form, equipmentId: e.target.value })}
                                helperText={approveRow.equipment_id ? `Matched on ${approveRow.matched_on || 'id'}` : 'No machine auto-matched — enter the equipment id'}
                            />
                            <TextField select label="New status" value={form.toStatus} onChange={(e) => setForm({ ...form, toStatus: e.target.value })}>
                                {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                            </TextField>
                            <TextField
                                type="date" label="Effective date" InputLabelProps={{ shrink: true }}
                                value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                            />
                            {isRentalTarget && (
                                <TextField
                                    label="Rental contract id" value={form.rentalContractId}
                                    onChange={(e) => setForm({ ...form, rentalContractId: e.target.value })}
                                    helperText="Required to attach the machine to a contract when putting it On Rent"
                                />
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveRow(null)} disabled={busy}>Cancel</Button>
                    <Button onClick={submitApprove} variant="contained" color="success" disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <ApproveIcon />}>
                        Approve & Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject dialog */}
            <Dialog open={Boolean(rejectRow)} onClose={() => !busy && setRejectRow(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Reject request</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{rejectRow?.subject}</Typography>
                    <TextField fullWidth multiline minRows={2} label="Reason (optional)" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectRow(null)} disabled={busy}>Cancel</Button>
                    <Button onClick={submitReject} variant="contained" color="error" disabled={busy}>Reject</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} variant="filled">{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
